import { useContext, useMemo, useCallback, useEffect, useState } from 'react'
import { css } from '@emotion/react'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import Collapse from '@mui/material/Collapse'
import { object } from 'prop-types'
import {
  CvsAutoComplete,
  CvsAutoCompleteAsync,
  CvsTextField,
  CvsAlert,
  CvsFieldMapper,
} from '@components'
import { connectorContext } from '@contexts'
import { api } from 'api'
import { templateCopyContext } from '../context'

const fullWidth = css`
  width: 100%;
`

const marginTop = css`
  margin-top: 12px;
`

export const Workfront = ({ assignment }) => {
  const { template, onChange } = useContext(templateCopyContext)
  const { byId } = useContext(connectorContext)
  const [templateOptions, setTemplateOptions] = useState([])
  const [taskOptions, setTaskOptions] = useState([])
  const [mapFromOptions, setMapFromOptions] = useState([])
  const [hovered, setHovered] = useState(false)

  const mapToOptions = useMemo(
    () => Object.values(template.fieldsById).map(f => ({ label: f.name, value: f.id })),
    [template],
  )
  const onWorkfrontChange = useCallback(
    newValues => {
      onChange(old => ({
        ...old,
        [assignment.id]: {
          ...old[assignment.id],
          ...newValues,
        },
      }))
    },
    [assignment, onChange],
  )

  const loadTemplateOptions = useCallback(
    searchTerm =>
      api
        .post(`/api/v1/integrations/workfront/command`, {
          command: 'getTemplatesByName',
          connector_id: assignment.connector_id,
          data: {
            name: searchTerm,
          },
        })
        .then(({ data }) => {
          setTemplateOptions(data ? data.map(opt => ({ value: opt.ID, label: opt.name })) : [])
          return data
        }),
    [assignment, template],
  )

  const loadTemplateTaskOptions = useCallback(
    searchTerm =>
      api
        .post(`/api/v1/integrations/workfront/command`, {
          command: 'getTemplateTasksByTemplate',
          connector_id: assignment.connector_id,
          data: {
            templateId: assignment.metadata.workfrontTemplateId,
            optionalParams: { name: searchTerm },
          },
        })
        .then(({ data }) => {
          setTaskOptions(data ? data.map(d => ({ value: d.ID, label: d.name })) : [])
          return data
        }),
    [assignment, template],
  )

  const loadTemplateFieldOptions = useCallback(
    () =>
      api
        .post(`/api/v1/integrations/workfront/command`, {
          command: 'getTemplate',
          connector_id: assignment.connector_id,
          data: {
            id: assignment.metadata.workfrontTemplateId,
          },
        })
        .then(({ data }) => {
          const fieldOptions = data
            ? data.objectCategories.flatMap(objCat =>
                objCat.category.categoryParameters.map(cp => ({
                  label: cp.parameter.name,
                  value: cp.parameter.ID,
                })),
              )
            : []
          setMapFromOptions(fieldOptions)
          return data
        }),
    [assignment, template],
  )

  const connectors = useMemo(
    () =>
      Object.values(byId)
        .filter(
          connector =>
            connector.connector_type_id ===
            (assignment.connector_type_id
              ? assignment.connector_type_id
              : byId[assignment.connector_id].connector_type_id),
        )
        .map(({ name, id }) => ({
          label: name,
          value: id,
        })),
    [assignment, byId],
  )

  const onWorkfrontMetadataChange = useCallback(
    newMetadata => {
      onChange(old => ({
        ...old,
        [assignment.id]: {
          ...old[assignment.id],
          metadata: {
            ...old[assignment.id].metadata,
            ...newMetadata,
          },
        },
      }))
    },
    [onChange, assignment],
  )

  useEffect(() => {
    if (assignment.connector_id) {
      loadTemplateOptions()
    }
  }, [assignment.connector_id])

  useEffect(() => {
    if (assignment.connector_id) {
      loadTemplateTaskOptions()
      loadTemplateFieldOptions()
    }
  }, [assignment.metadata.workfrontTemplateId])

  return (
    <div css={fullWidth}>
      <CvsTextField
        css={fullWidth}
        label={t`Integration name`}
        onChange={e =>
          onWorkfrontChange({
            name: e.target.value,
          })
        }
        value={assignment.name}
      />
      <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
        <Collapse in={hovered}>
          <div css={marginTop} />
          <CvsAlert>
            <Trans>
              Changing the workfront account, template or template task values will remove{' '}
              <b>all existing task subscriptions</b> for this Workfront integration. As a result,
              changing a task's status in Workfront for projects previously associated with this
              integration will have no effect.
            </Trans>
          </CvsAlert>
        </Collapse>
      </div>

      <CvsAutoComplete
        options={connectors}
        onSelected={selected => {
          if (selected !== assignment.connector_id) {
            onWorkfrontChange({ connector_id: selected })
            setTemplateOptions([])
            setTaskOptions([])
          }
        }}
        selected={assignment.connector_id}
        label={t`Workfront account`}
      />
      <CvsAutoCompleteAsync
        fullWidth
        options={templateOptions}
        onSelected={selected => {
          if (selected !== assignment.metadata.workfrontTemplateId) {
            onWorkfrontMetadataChange({
              workfrontTemplateId: selected,
              workfrontTemplateTaskId: '',
            })
            setTaskOptions([])
          }
        }}
        selected={assignment.metadata.workfrontTemplateId}
        label={t`Workfront template`}
        loadOptions={loadTemplateOptions}
      />

      <CvsAutoCompleteAsync
        fullWidth
        options={taskOptions}
        onSelected={selected =>
          onWorkfrontMetadataChange({
            workfrontTemplateTaskId: selected,
          })
        }
        selected={assignment.metadata.workfrontTemplateTaskId}
        label={t`Workfront template task`}
        loadOptions={loadTemplateTaskOptions}
        disabled={!assignment.metadata.workfrontTemplateId}
      />
      <CvsFieldMapper
        fromOptions={mapFromOptions}
        toOptions={mapToOptions}
        requiredMappings={[]}
        value={(assignment.metadata.campaignDataMappedFields || []).reduce(
          (acc, mf) => ({ ...acc, [mf.workfrontParameterId]: mf.claravineTemplateFieldId }),
          {},
        )}
        onSelect={value => {
          const data = Object.entries(value).map(([workfrontId, claravineId]) => ({
            claravineTemplateFieldId: claravineId,
            claravineTemplateFieldName: template.fieldsById[claravineId]?.name || '',
            workfrontParameterId: workfrontId,
            workfrontParameterName: mapFromOptions.find(o => o.value === workfrontId)?.label || '',
          }))
          onWorkfrontMetadataChange({
            campaignDataMappedFields: data,
          })
        }}
      />
    </div>
  )
}

Workfront.propTypes = {
  assignment: object.isRequired,
}
